import { useEffect, useState } from "react";
import { API } from "@/App";

/**
 * In-memory cached fetch of the BEAR-ENT catalog tree.
 * Shared across every nav/dropdown component on the page so we don't
 * refetch on every hover.
 */
let _cache = null;
let _inflight = null;

async function fetchBearEntTree() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = fetch(`${API}/bear-ent/catalog`).then(async r => {
    if (!r.ok) throw new Error("Failed to load ENT catalog");
    const data = await r.json();
    const tree = (data.categories || []).map(c => {
      // Tally instrument-type sub-tags within this category
      const typeMap = {};
      for (const p of (c.products || [])) {
        for (const t of (p.sub_tags || [])) {
          typeMap[t] = (typeMap[t] || 0) + 1;
        }
      }
      const types = Object.entries(typeMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name));
      return {
        slug: c.slug,
        name: c.name,
        count: (c.products || []).length,
        types,
      };
    });
    _cache = tree;
    _inflight = null;
    return tree;
  }).catch((e) => { _inflight = null; throw e; });
  return _inflight;
}

/** Hook: returns the BEAR-ENT category tree (with sub-tag counts). */
export function useBearEntTree() {
  const [tree, setTree] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) { setTree(_cache); setLoading(false); return; }
    fetchBearEntTree()
      .then((t) => { setTree(t); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  return { tree, loading };
}
